import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Users, Link2, Copy, Check, RefreshCw, Heart, ArrowLeft, Sparkles } from 'lucide-react';
import { computeResult } from '../utils/scoringEngine';
import { questions } from '../data/questionsData';
import QuizRunner from './QuizRunner';
import BussolaDoEncontro from './BussolaDoEncontro';

// Em produção, usa URL relativa. Em preview, usa a variável de ambiente.
const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Se estiver em produção (não é preview), usa URL relativa
  if (typeof window !== 'undefined' && !window.location.hostname.includes('preview.emergentagent.com')) {
    return '';
  }
  return envUrl;
};
const BACKEND_URL = getBackendUrl();

// Gera respostas demo para um perfil específico
const gerarRespostasDemo = (tipo) => {
  const respostas = {};
  questions.forEach((q, i) => {
    // Gera valores baseados no tipo de perfil
    let val = 3;
    const qRoot = q.root || 'peregrino';
    
    if (tipo === 'peregrino') {
      val = qRoot === 'peregrino' ? 5 : (qRoot === 'guardiao' ? 2 : 3);
    } else if (tipo === 'guardiao') {
      val = qRoot === 'guardiao' ? 5 : (qRoot === 'peregrino' ? 2 : 3);
    } else if (tipo === 'amante') {
      val = qRoot === 'amante' ? 5 : (qRoot === 'alquimista' ? 2 : 3);
    } else if (tipo === 'alquimista') {
      val = qRoot === 'alquimista' ? 5 : (qRoot === 'amante' ? 2 : 3);
    } else {
      // Aleatório
      val = Math.floor(Math.random() * 5) + 1;
    }
    // Adiciona um pouco de variação
    const variacao = Math.random() > 0.7 ? (Math.random() > 0.5 ? 1 : -1) : 0;
    val = Math.max(1, Math.min(5, val + variacao));
    respostas[i + 1] = val;
  });
  return respostas;
};

// Perfis de casais para demonstração
const DEMO_CASAIS = [
  { nomeA: 'Marina', nomeB: 'Lucas', tipoA: 'amante', tipoB: 'guardiao' },
  { nomeA: 'Sofia', nomeB: 'Pedro', tipoA: 'peregrino', tipoB: 'alquimista' },
  { nomeA: 'Ana', nomeB: 'Gabriel', tipoA: 'guardiao', tipoB: 'amante' },
  { nomeA: 'Julia', nomeB: 'Rafael', tipoA: 'alquimista', tipoB: 'peregrino' },
  { nomeA: 'Beatriz', nomeB: 'Thiago', tipoA: 'random', tipoB: 'random' },
];

// Fluxo do Modo Relacional. Rotas: /encontro  e  /encontro/:codigo
const ModoEncontro = () => {
  const { codigo: codigoParam } = useParams();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  
  const [phase, setPhase] = useState(codigoParam ? 'carregando_par' : (isDemo ? 'demo' : 'intro'));
  const [nomeA, setNomeA] = useState('');
  const [nomeB, setNomeB] = useState('');
  const [respA, setRespA] = useState(null);
  const [codigo, setCodigo] = useState(codigoParam || null);
  const [resultado, setResultado] = useState(null); // { rA, rB, nomeA, nomeB }
  const [erro, setErro] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const fundo = "min-h-screen bg-gradient-to-br from-[#160f2e] via-indigo-950 to-[#0a1628] flex items-center justify-center px-4 py-10";
  const card = "max-w-md w-full bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 text-center";
  const input = "w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-rose-400 transition-all mb-3";

  const montarResultado = (rA, rB, nA, nB, cod) => { setResultado({ rA, rB, nomeA: nA, nomeB: nB, codigo: cod || null }); setPhase('resultado'); };

  // ---- DEMO: Gera resultado de casal aleatório ----
  const gerarDemo = () => {
    const casal = DEMO_CASAIS[Math.floor(Math.random() * DEMO_CASAIS.length)];
    const respDemoA = gerarRespostasDemo(casal.tipoA);
    const respDemoB = gerarRespostasDemo(casal.tipoB);
    const rA = computeResult(respDemoA, questions);
    const rB = computeResult(respDemoB, questions);
    montarResultado(rA, rB, casal.nomeA, casal.nomeB, 'DEMO');
  };

  // ---- lado de quem foi convidado (B): carregar o par ----
  useEffect(() => {
    if (!codigoParam) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/encontros/${codigoParam}`);
        if (!res.ok) { setErro('Convite não encontrado ou expirado.'); setPhase('erro'); return; }
        const par = await res.json();
        setNomeA(par.nome_a || 'Alguém');
        if (par.completo && par.respostas_a && par.respostas_b) {
          montarResultado(computeResult(par.respostas_a, questions), computeResult(par.respostas_b, questions), par.nome_a, par.nome_b, codigo);
        } else {
          setPhase('join_intro');
        }
      } catch (e) { setErro('Não foi possível carregar o convite.'); setPhase('erro'); }
    })();
  }, [codigoParam]);

  // ---- JUNTOS: A termina -> passa o celular ----
  const aTerminouJuntos = (answers) => { setRespA(answers); setPhase('pass'); };
  const bTerminouJuntos = async (answers) => {
    const rA = computeResult(respA, questions);
    const rB = computeResult(answers, questions);
    let cod = null;
    try {
      const res = await fetch(`${BACKEND_URL}/api/encontros/completo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome_a: nomeA.trim(), respostas_a: respA, nome_b: nomeB.trim(), respostas_b: answers }),
      });
      const data = await res.json();
      if (res.ok && data.codigo) cod = data.codigo;
    } catch (e) { /* mostra a leitura mesmo sem o link */ }
    montarResultado(rA, rB, nomeA.trim(), nomeB.trim(), cod);
  };

  // ---- À DISTÂNCIA: A termina -> cria o par ----
  const aTerminouDistancia = async (answers) => {
    setCarregando(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/encontros/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome_a: nomeA.trim(), respostas_a: answers }),
      });
      const data = await res.json();
      if (res.ok && data.codigo) { setCodigo(data.codigo); setPhase('convite'); }
      else { setErro('Não foi possível criar o convite.'); setPhase('erro'); }
    } catch (e) { setErro('Falha de conexão ao criar o convite.'); setPhase('erro'); }
    finally { setCarregando(false); }
  };

  // A confere se o par já respondeu
  const conferirConvite = async () => {
    setCarregando(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/encontros/${codigo}`);
      const par = await res.json();
      if (par.completo) montarResultado(computeResult(par.respostas_a, questions), computeResult(par.respostas_b, questions), par.nome_a, par.nome_b, codigo);
      else alert('A outra pessoa ainda não respondeu. Assim que responder, é só clicar de novo. 🌱');
    } catch (e) { alert('Não foi possível conferir agora.'); }
    finally { setCarregando(false); }
  };

  // ---- B responde e junta ----
  const bTerminouJoin = async (answers) => {
    setCarregando(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/encontros/${codigo}/join`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome_b: nomeB.trim(), respostas_b: answers }),
      });
      if (!res.ok) { setErro('Não foi possível registrar suas respostas.'); setPhase('erro'); return; }
      const r2 = await fetch(`${BACKEND_URL}/api/encontros/${codigo}`);
      const par = await r2.json();
      montarResultado(computeResult(par.respostas_a, questions), computeResult(par.respostas_b, questions), par.nome_a, par.nome_b, codigo);
    } catch (e) { setErro('Falha de conexão.'); setPhase('erro'); }
    finally { setCarregando(false); }
  };

  const copiarLink = async () => {
    const link = `${window.location.origin}/encontro/${codigo}`;
    try { await navigator.clipboard.writeText(link); setCopiado(true); setTimeout(()=>setCopiado(false), 2000); } catch (e) {}
  };

  // ===================== RENDER =====================
  if (phase === 'resultado' && resultado)
    return <BussolaDoEncontro resultA={resultado.rA} resultB={resultado.rB} nomeA={resultado.nomeA} nomeB={resultado.nomeB} codigo={resultado.codigo} />;

  if (phase === 'erro')
    return <div className={fundo}><div className={card}><div className="text-5xl mb-3">🌙</div><h2 className="text-xl font-bold text-white mb-2">Ops</h2><p className="text-white/70">{erro}</p><a href="/" className="inline-block mt-5 px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20">Voltar ao início</a></div></div>;

  // DEMO — tela de demonstração
  if (phase === 'demo')
    return (
      <div className={fundo}>
        <div className="max-w-lg w-full bg-gradient-to-br from-rose-900/30 via-purple-900/20 to-indigo-900/30 backdrop-blur-sm rounded-2xl p-8 border border-rose-500/30 text-center">
          <div className="inline-flex w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full items-center justify-center mb-5 shadow-lg shadow-rose-500/30">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Demonstração da Bússola</h2>
          <p className="text-white/70 text-sm mb-6">Clique para gerar um resultado de casal aleatório. Ideal para apresentações!</p>
          
          <button 
            onClick={gerarDemo}
            className="w-full p-4 rounded-xl bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold text-lg hover:opacity-90 transition-all mb-4 shadow-lg shadow-rose-500/30"
            style={{ border: 'none', cursor: 'pointer' }}
            data-testid="gerar-demo-encontro"
          >
            💕 Gerar Resultado de Casal
          </button>
          
          <div className="text-white/50 text-xs space-y-1 mb-5">
            <p>Casais de demonstração com diferentes dinâmicas:</p>
            <p className="text-rose-300/60">Amante + Guardião • Peregrino + Alquimista • Aleatório</p>
          </div>
          
          <div className="flex gap-3 justify-center">
            <a href="/encontro" className="px-5 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 text-sm">Fazer teste real</a>
            <a href="/" className="px-5 py-2 bg-white/5 text-white/70 rounded-lg hover:bg-white/10 text-sm">Voltar ao início</a>
          </div>
        </div>
      </div>
    );

  if (phase === 'carregando_par')
    return <div className={fundo}><div className={card}><div className="w-10 h-10 border-2 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-white/70">Abrindo o convite…</p></div></div>;

  // QUIZ screens
  if (phase === 'quizA_juntos') return <QuizRunner titulo={`A vez de ${nomeA || 'você'}`} subtitulo="Responda com sinceridade" onComplete={aTerminouJuntos} onBack={() => setPhase('nomes_juntos')} />;
  if (phase === 'quizB_juntos') return <QuizRunner titulo={`A vez de ${nomeB || 'você'}`} subtitulo="Responda com sinceridade" onComplete={bTerminouJuntos} />;
  if (phase === 'quizA_dist')   return <QuizRunner titulo={`A vez de ${nomeA || 'você'}`} subtitulo="Depois você convida a outra pessoa" onComplete={aTerminouDistancia} onBack={() => setPhase('nome_a')} />;
  if (phase === 'quizB_join')   return <QuizRunner titulo={`A vez de ${nomeB || 'você'}`} subtitulo={`${nomeA} já respondeu — falta você`} onComplete={bTerminouJoin} />;

  return (
    <div className={fundo}>
      {/* INTRO — escolher o modo */}
      {phase === 'intro' && (
        <div className={card}>
          <div className="inline-flex w-16 h-16 bg-gradient-to-br from-rose-400 to-indigo-400 rounded-full items-center justify-center mb-4"><Heart className="w-8 h-8 text-white" /></div>
          <h2 className="text-2xl font-bold text-white mb-1">A Bússola do Encontro</h2>
          <p className="text-white/70 text-sm mb-6">Descubram como dois corações se encontram. Como vocês querem fazer?</p>
          <button onClick={() => setPhase('nomes_juntos')} className="w-full mb-3 p-4 rounded-xl bg-gradient-to-r from-rose-500/20 to-pink-500/20 border border-rose-400/30 text-left hover:from-rose-500/30" style={{ cursor:'pointer' }}>
            <div className="flex items-center gap-3"><Users className="w-6 h-6 text-rose-300" /><div><p className="text-white font-semibold">Juntos, agora</p><p className="text-white/60 text-xs">Os dois respondem no mesmo aparelho</p></div></div>
          </button>
          <button onClick={() => setPhase('nome_a')} className="w-full p-4 rounded-xl bg-gradient-to-r from-indigo-500/20 to-blue-500/20 border border-indigo-400/30 text-left hover:from-indigo-500/30" style={{ cursor:'pointer' }}>
            <div className="flex items-center gap-3"><Link2 className="w-6 h-6 text-indigo-300" /><div><p className="text-white font-semibold">À distância</p><p className="text-white/60 text-xs">Convide alguém por um link</p></div></div>
          </button>
          <a href="/" className="inline-block mt-5 text-white/50 text-sm hover:text-white">Voltar ao teste</a>
        </div>
      )}

      {/* JUNTOS — nomes */}
      {phase === 'nomes_juntos' && (
        <div className={card}>
          <Users className="w-10 h-10 text-rose-300 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-white mb-4">Quem são vocês dois?</h2>
          <input className={input} placeholder="Primeiro nome de quem começa" value={nomeA} onChange={e=>setNomeA(e.target.value)} />
          <input className={input} placeholder="Primeiro nome da outra pessoa" value={nomeB} onChange={e=>setNomeB(e.target.value)} />
          <button disabled={!nomeA.trim() || !nomeB.trim()} onClick={() => setPhase('quizA_juntos')}
            className={`w-full py-3 rounded-xl font-semibold mt-2 ${nomeA.trim()&&nomeB.trim() ? 'bg-gradient-to-r from-rose-500 to-indigo-500 text-white' : 'bg-white/20 text-white/50 cursor-not-allowed'}`} style={{ border:'none' }}>
            Começar — vez de {nomeA.trim() || '...'}
          </button>
          <button onClick={() => setPhase('intro')} className="mt-3 text-white/50 text-sm hover:text-white flex items-center gap-1 mx-auto"><ArrowLeft className="w-4 h-4" /> Voltar</button>
        </div>
      )}

      {/* JUNTOS — passar o celular */}
      {phase === 'pass' && (
        <div className={card}>
          <div className="text-5xl mb-3">🤝</div>
          <h2 className="text-2xl font-bold text-white mb-2">Agora é a vez de {nomeB} 💛</h2>
          <p className="text-white/70 text-sm mb-6">Passe o celular para {nomeB} responder. Sem espiar as respostas um do outro!</p>
          <button onClick={() => setPhase('quizB_juntos')} className="w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-rose-500 to-indigo-500 text-white" style={{ border:'none' }}>
            Estou pronto(a) — sou {nomeB}
          </button>
        </div>
      )}

      {/* À DISTÂNCIA — nome de A */}
      {phase === 'nome_a' && (
        <div className={card}>
          <Link2 className="w-10 h-10 text-indigo-300 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-white mb-1">Vamos começar por você</h2>
          <p className="text-white/60 text-sm mb-4">Você responde primeiro e depois gera um link para convidar a outra pessoa.</p>
          <input className={input} placeholder="Seu primeiro nome" value={nomeA} onChange={e=>setNomeA(e.target.value)} />
          <button disabled={!nomeA.trim()} onClick={() => setPhase('quizA_dist')}
            className={`w-full py-3 rounded-xl font-semibold mt-1 ${nomeA.trim() ? 'bg-gradient-to-r from-indigo-500 to-blue-500 text-white' : 'bg-white/20 text-white/50 cursor-not-allowed'}`} style={{ border:'none' }}>
            Responder o teste
          </button>
          <button onClick={() => setPhase('intro')} className="mt-3 text-white/50 text-sm hover:text-white flex items-center gap-1 mx-auto"><ArrowLeft className="w-4 h-4" /> Voltar</button>
        </div>
      )}

      {/* À DISTÂNCIA — convite gerado */}
      {phase === 'convite' && (
        <div className={card}>
          <div className="inline-flex w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full items-center justify-center mb-4"><Check className="w-8 h-8 text-white" /></div>
          <h2 className="text-xl font-bold text-white mb-1">Pronto, {nomeA}!</h2>
          <p className="text-white/70 text-sm mb-5">Envie este link para a outra pessoa. Quando ela responder, a Bússola do Encontro aparece para os dois.</p>
          <div className="bg-white/5 rounded-xl p-3 border border-white/10 mb-3 break-all text-white/80 text-sm">{window.location.origin}/encontro/{codigo}</div>
          <button onClick={copiarLink} className="w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-indigo-500 to-blue-500 text-white flex items-center justify-center gap-2 mb-3" style={{ border:'none' }}>
            {copiado ? <><Check className="w-5 h-5" /> Copiado!</> : <><Copy className="w-5 h-5" /> Copiar link do convite</>}
          </button>
          <button onClick={conferirConvite} disabled={carregando} className="w-full py-3 rounded-xl font-semibold bg-white/10 text-white hover:bg-white/20 flex items-center justify-center gap-2" style={{ border:'none' }}>
            <RefreshCw className={`w-4 h-4 ${carregando?'animate-spin':''}`} /> Já respondeu? Ver o resultado
          </button>
        </div>
      )}

      {/* JOIN — B chegou pelo link */}
      {phase === 'join_intro' && (
        <div className={card}>
          <div className="text-5xl mb-3">💞</div>
          <h2 className="text-xl font-bold text-white mb-1">{nomeA} te convidou</h2>
          <p className="text-white/70 text-sm mb-4">para descobrir como os corações de vocês dois se encontram. Responda o teste para revelar a Bússola do Encontro.</p>
          <input className={input} placeholder="Seu primeiro nome" value={nomeB} onChange={e=>setNomeB(e.target.value)} />
          <label className="flex items-start gap-2 text-white/60 text-xs text-left mb-3">
            <input type="checkbox" id="consent" className="mt-0.5 accent-rose-500" defaultChecked />
            <span>Topo dividir a minha leitura com {nomeA} para vermos o encontro de nós dois.</span>
          </label>
          <button disabled={!nomeB.trim()} onClick={() => setPhase('quizB_join')}
            className={`w-full py-3 rounded-xl font-semibold ${nomeB.trim() ? 'bg-gradient-to-r from-rose-500 to-indigo-500 text-white' : 'bg-white/20 text-white/50 cursor-not-allowed'}`} style={{ border:'none' }}>
            Responder o teste
          </button>
        </div>
      )}
    </div>
  );
};
export default ModoEncontro;
